import type { ParseKeys } from 'i18next';
import React, { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/Button';
import { Calendar } from '@/components/Icons/Calendar';
import { CalendarDeadline } from '@/components/Icons/CalendarDeadline';
import { CheckCircleFilled } from '@/components/Icons/CheckCircleFilled';
import { ClockFilled } from '@/components/Icons/ClockFilled';
import { EyeFilled } from '@/components/Icons/EyeFilled';
import { EyeOffFilled } from '@/components/Icons/EyeOffFilled';
import { GraphBar } from '@/components/Icons/GraphBar';
import {
  BorderRadius,
  ButtonVariant,
  Size,
  SurveyAnonymity,
  SurveyDisplayStatus,
} from '@/types/components';
import type { Survey } from '@/types/survey';
import { stopPropagation } from '@/utils/global';

const statusChip: Record<
  SurveyDisplayStatus,
  { labelKey: ParseKeys; className: string; icon: ReactNode }
> = {
  [SurveyDisplayStatus.ToFill]: {
    labelKey: 'mail.surveys.status.toFill',
    className: 'bg-warning-surface',
    icon: <ClockFilled className="size-4 shrink-0 text-warning" />,
  },
  [SurveyDisplayStatus.Filled]: {
    labelKey: 'mail.surveys.status.filled',
    className: 'bg-success-surface',
    icon: <CheckCircleFilled className="size-4 shrink-0" />,
  },
  [SurveyDisplayStatus.Closed]: {
    labelKey: 'mail.surveys.status.closed',
    className: 'bg-on-primary-surface',
    icon: (
      <CalendarDeadline className="size-4 shrink-0 text-subtle-foreground" />
    ),
  },
};

const anonymityChip: Record<
  SurveyAnonymity,
  { labelKey: ParseKeys; icon: ReactNode }
> = {
  [SurveyAnonymity.Personalized]: {
    labelKey: 'mail.surveys.type.personalized',
    icon: <EyeFilled className="size-4 shrink-0 text-primary" />,
  },
  [SurveyAnonymity.Anonymous]: {
    labelKey: 'mail.surveys.type.anonymous',
    icon: <EyeOffFilled className="size-4 shrink-0 text-primary" />,
  },
};

interface Props {
  survey: Survey;
  isMobile: boolean;
  isSummary?: boolean;
  onOpen?: (id: string) => void;
  onFill?: (id: string) => void;
}

export const SurveyCard: React.FC<Props> = ({
  survey,
  isMobile,
  isSummary,
  onOpen,
  onFill,
}) => {
  const { t } = useTranslation();

  const {
    displayStatus,
    anonymity,
    title,
    authorName,
    date,
    deadline,
    hasResults,
  } = survey;
  const isToFill = displayStatus === SurveyDisplayStatus.ToFill;
  const showFillButton = isToFill && !isSummary;
  const statusConfig = displayStatus && statusChip[displayStatus];
  const anonymityConfig = anonymityChip[anonymity];

  const smallText = isMobile ? 'text-body-13' : 'text-body-14';
  const nameText = isMobile ? 'text-caption-13' : 'text-body-14';

  const chipRow = (
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-2">
        {statusConfig && (
          <span
            className={`inline-flex items-center gap-1 rounded-xl px-2 py-0.5 ${statusConfig.className}`}
          >
            {statusConfig.icon}
            <span className="text-caption-12 whitespace-nowrap text-foreground">
              {t(statusConfig.labelKey)}
            </span>
          </span>
        )}
        <span className="inline-flex items-center gap-1 rounded-xl bg-purple-soft px-2 py-0.5">
          {anonymityConfig.icon}
          <span className="text-caption-12 whitespace-nowrap text-foreground">
            {t(anonymityConfig.labelKey)}
          </span>
        </span>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        <span
          className={`${smallText} whitespace-nowrap text-subtle-foreground`}
        >
          {date}
        </span>
        {hasResults && !isSummary && (
          <>
            <span className="h-[18px] w-px bg-border-strong" />
            <GraphBar className="size-4 shrink-0 text-primary" />
          </>
        )}
      </div>
    </div>
  );

  const textBlock = (
    <div className="flex min-w-0 flex-col gap-3">
      <div className="flex min-w-0 flex-col gap-1">
        <p
          className={`text-foreground ${isSummary ? '' : 'truncate'} ${isToFill ? 'text-callout-16' : 'text-body-16'}`}
        >
          {title}
        </p>
        <p className={`truncate text-muted-foreground ${nameText}`}>
          {authorName}
        </p>
      </div>

      <div
        className={`flex items-center gap-1 text-subtle-foreground ${smallText}`}
      >
        <Calendar className="size-4 shrink-0" />
        <span>{t('mail.surveys.deadline', { deadline })}</span>
      </div>
    </div>
  );

  const fillButton = (
    <div onClick={stopPropagation}>
      <Button
        variant={ButtonVariant.Primary}
        size={isMobile ? Size.Large : Size.Small}
        radius={isMobile ? BorderRadius.Pill : undefined}
        className={isMobile ? 'w-full' : ''}
        onClick={() => onFill?.(survey.id)}
      >
        {t('mail.surveys.fill')}
      </Button>
    </div>
  );

  if (isMobile) {
    return (
      <div
        onClick={() => onOpen?.(survey.id)}
        className={`flex flex-col gap-5 rounded-xl bg-card p-4 shadow-card ${isSummary ? '' : 'cursor-pointer transition-colors hover:bg-hover'}`}
      >
        <div className="flex flex-col gap-3">
          {chipRow}
          {textBlock}
        </div>

        {showFillButton && fillButton}
      </div>
    );
  }

  return (
    <div
      onClick={() => onOpen?.(survey.id)}
      className={`flex flex-col gap-3 rounded-xl bg-card p-4 shadow-card ${isSummary ? '' : 'cursor-pointer transition-colors hover:bg-hover'}`}
    >
      {chipRow}

      <div className="flex items-end gap-3">
        <div className="min-w-0 flex-1">{textBlock}</div>
        {showFillButton && fillButton}
      </div>
    </div>
  );
};
